import React from 'react';
import { BsArchive, BsBoxArrowInUpLeft, BsCheckCircle, BsClockHistory, BsExclamationTriangle, BsFlagFill, BsTrash, BsXSquare } from 'react-icons/bs';

import { analyticsIds } from '../../constants/analyticsIds';
import { inAppText } from '../../constants/inAppText';
import { Statuses } from '../../constants/statuses';
import { createErrorToast } from '../../redux/modules/app';
import { hideModal, showModal, updateModalOptions } from '../../redux/modules/modal';
import { arrayFilterAndMap } from '../../utils/arrayUtil';
import ConfirmActionWarningModal from '../ConfirmActionWarningModal';
import StackedButtonGroup from '../Modal/ModalButtonGroups/StackedButtonGroup';

const globalStatusOptions = {
  [Statuses.Active]: {
    status: Statuses.Active,
    arrowColor: 'white',
    backgroundColor: 'success.primary.600',
    fontColor: 'white',
    hoverBackgroundColor: 'success.primary.300',
    statusText: 'Active',
    actionText: 'Make Active',
    iconColor: 'success.primary.600',
    icon: BsCheckCircle
  },
  [Statuses.Approved]: {
    status: Statuses.Approved,
    arrowColor: 'white',
    backgroundColor: 'success.primary.600',
    fontColor: 'white',
    hoverBackgroundColor: 'success.primary.300',
    statusText: 'Approved',
    actionText: 'Approve',
    iconColor: 'success.primary.600',
    icon: BsCheckCircle
  },
  [Statuses.Archived]: {
    status: Statuses.Archived,
    arrowColor: 'greyScale.900',
    backgroundColor: 'transparent',
    fontColor: 'greyScale.800',
    hoverBackgroundColor: 'greyScale.50',
    statusText: 'Archived',
    actionText: 'Archive',
    icon: BsArchive,
    iconColor: 'greyScale.800'
  },
  [Statuses.Created]: {
    status: Statuses.Created,
    arrowColor: 'greyScale.900',
    backgroundColor: 'transparent',
    fontColor: 'greyScale.800',
    hoverBackgroundColor: 'greyScale.50',
    statusText: 'Created',
    actionText: 'Create',
    iconColor: 'success.primary.600'
  },
  [Statuses.Canceled]: {
    status: Statuses.Canceled,
    arrowColor: 'white',
    backgroundColor: 'greyScale.900',
    fontColor: 'white',
    hoverBackgroundColor: 'greyScale.800',
    statusText: 'Canceled',
    actionText: 'Cancel',
    iconColor: 'caution.primary.600',
    icon: BsXSquare
  },
  [Statuses.Compliant]: {
    status: Statuses.Compliant,
    arrowColor: 'white',
    backgroundColor: 'success.primary.600',
    fontColor: 'white',
    hoverBackgroundColor: 'success.primary.300',
    statusText: 'Compliant',
    actionText: 'Mark Compliant',
    icon: BsCheckCircle,
    iconColor: 'success.primary.600'
  },
  [Statuses.Deleted]: {
    status: Statuses.Deleted,
    arrowColor: 'greyScale.900',
    backgroundColor: 'transparent',
    fontColor: 'greyScale.800',
    hoverBackgroundColor: 'greyScale.50',
    statusText: 'Deleted',
    actionText: 'Delete',
    icon: BsTrash,
    iconColor: 'greyScale.800'
  },
  [Statuses.Expired]: {
    status: Statuses.Expired,
    arrowColor: 'white',
    backgroundColor: 'error.primary.600',
    fontColor: 'white',
    hoverBackgroundColor: 'error.primary.900',
    statusText: 'Expired'
  },
  [Statuses.NonCompliant]: {
    status: Statuses.NonCompliant,
    arrowColor: 'white',
    backgroundColor: 'caution.primary.600',
    fontColor: 'white',
    hoverBackgroundColor: 'caution.primary.900',
    statusText: 'Non-Compliant',
    actionText: 'Mark Non-Compliant',
    iconColor: 'error.primary.600',
    icon: BsExclamationTriangle
  },
  [Statuses.NotApproved]: {
    status: Statuses.NotApproved,
    arrowColor: 'white',
    backgroundColor: 'error.primary.600',
    fontColor: 'white',
    hoverBackgroundColor: 'error.primary.900',
    statusText: 'Not Approved',
    actionText: 'Mark Not Approved',
    iconColor: 'error.primary.600',
    icon: BsExclamationTriangle
  },
  [Statuses.NotReceived]: {
    status: Statuses.NotReceived,
    arrowColor: 'greyScale.900',
    backgroundColor: 'transparent',
    fontColor: 'greyScale.900',
    hoverBackgroundColor: 'greyScale.50',
    statusText: 'Not Received',
    iconColor: 'error.primary.600',
    icon: BsExclamationTriangle
  },
  [Statuses.Required]: {
    status: Statuses.Required,
    arrowColor: 'greyScale.900',
    backgroundColor: 'transparent',
    fontColor: 'greyScale.900',
    hoverBackgroundColor: 'greyScale.50',
    statusText: 'Not Received',
    iconColor: 'error.primary.600',
    icon: BsExclamationTriangle
  },
  [Statuses.Pending]: {
    status: Statuses.Pending,
    arrowColor: 'white',
    backgroundColor: 'primaryScale.500',
    fontColor: 'white',
    hoverBackgroundColor: 'primaryScale.600',
    statusText: 'Pending',
    actionText: 'Mark Pending',
    iconColor: 'caution.primary.600',
    icon: BsClockHistory
  },
  [Statuses.Received]: {
    status: Statuses.Received,
    arrowColor: 'greyScale.900',
    backgroundColor: 'transparent',
    fontColor: 'greyScale.900',
    hoverBackgroundColor: 'greyScale.50',
    statusText: 'Received',
    iconColor: 'greyScale.800',
    icon: BsClockHistory
  },
  [Statuses.Waived]: {
    status: Statuses.Waived,
    arrowColor: 'greyScale.800',
    backgroundColor: 'greyScale.50',
    fontColor: 'greyScale.800',
    hoverBackgroundColor: 'greyScale.100',
    statusText: 'Waived',
    actionText: 'Waive Requirement',
    iconColor: 'greyScale.800',
    icon: BsFlagFill
  },
  [Statuses.NotRequired]: {
    status: Statuses.NotRequired,
    arrowColor: 'greyScale.800',
    backgroundColor: 'greyScale.50',
    fontColor: 'greyScale.800',
    hoverBackgroundColor: 'greyScale.100',
    statusText: 'Not Required',
    iconColor: 'greyScale.800',
    icon: BsFlagFill
  },
  [Statuses.UploadedElsewhere]: {
    status: Statuses.UploadedElsewhere,
    arrowColor: 'white',
    backgroundColor: 'success.primary.600',
    fontColor: 'white',
    hoverBackgroundColor: 'success.primary.300',
    statusText: 'Uploaded Elsewhere',
    iconColor: 'success.primary.600',
    icon: BsCheckCircle
  }
};

const transitionHandlers = {
  requirements: {
    '*': {
      handler: async (status, dispatch, { policyId, policyTypeId, updateRequirementStatus }) => {
        await updateRequirementStatus({
          policyTypeId,
          policyId,
          status
        });
      }
    },
    [Statuses.Active]: {
      handler: async (value, dispatch, { reactivatePolicy }) => {
        await reactivatePolicy();
      }
    },
    [Statuses.Archived]: {
      handler: async (value, dispatch, { archivePolicy }) => {
        dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isLoading: true } } }));

        await archivePolicy();
      }
    },
    [Statuses.Deleted]: {
      handler: (value, dispatch, { deletePolicy }) => {
        dispatch(showModal(
          () => <ConfirmActionWarningModal description={inAppText.insuredProfile.policiesTable.deletePolicy} />,
          {
            title: 'Delete Insurance Policy?',
            size: 'md',
            buttons: {
              component: StackedButtonGroup,
              primaryButtonOptions: {
                label: 'Delete',
                isDisabled: true,
                onClick: async () => {
                  dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isLoading: true } } }));

                  await deletePolicy();

                  dispatch(hideModal());
                }
              }
            }
          }
        ));
      }
    },
    [Statuses.Canceled]: { handler: (value, dispatch) => dispatch(createErrorToast({ title: 'Not Implemented' })) }
  },
  notReceivedRequirements: {
    '*': {
      handler: async (status, dispatch, { policyId, policyTypeId, updateRequirementStatus }) => {
        await updateRequirementStatus({
          policyId,
          policyTypeId,
          status
        });
      }
    }
  },
  documents: {
    '*': {
      handler: async (status, dispatch, { documentTypeId, updateRequirementStatus }) => {
        await updateRequirementStatus({
          documentTypeId,
          status
        });
      }
    },
    [Statuses.Archived]: {
      handler: async (value, dispatch, { archiveDocument }) => {
        await archiveDocument();
      }
    },
    [Statuses.Active]: {
      handler: async (value, dispatch, { reactivateDocument }) => {
        await reactivateDocument();
      }
    },
    [Statuses.Deleted]: {
      handler: async (value, dispatch, { deleteDocument }) => {
        await deleteDocument();
      }
    }
  },
  jobs: {
    '*': {
      handler: async (status, dispatch, { insuredId, jobId }) => {
        // await dispatch(updateRequirementStatus({
        //   insuredId,
        //   jobId,
        //   status
        // }));
        //
        // dispatch(getCurrentJobStatus());
        // dispatch(getInsuredOverallStatus());
      }
    },
    [Statuses.Archived]: {
      handler: (value, dispatch, { requirementId, insuredId, jobId }) => {
        // const cookieId = 'hideArchiveRecordModal';
        //
        // const archiveRecord = async () => {
        //   dispatch(hideModal());
        //   createToast({
        //     title: 'Archiving Record...',
        //     status: 'info',
        //     duration: 10000
        //   });
        //
        //   await dispatch(archiveInsuranceRequirement(requirementId));
        //
        //   const pathWithoutJobs = window.location.pathname.replace(/(\/jobs.*\/\d+)?$/, '');
        //   window.history.replaceState(null, '', pathWithoutJobs);
        //
        //   dispatch(getInsuredOverallStatus());
        //   dispatch(getActiveJobs(insuredId));
        // };
        //
        // showDontAskAgainModal({
        //   dispatch,
        //   cookieId,
        //   description: inAppText.projectDetailsPage.recordOptions.archiveRecordMessage,
        //   onClick: archiveRecord,
        //   title: 'Archive Record?'
        // });
      }
    },
    [Statuses.Deleted]: {
      handler: (value, dispatch, { requirementId, insuredId }) => {
        // dispatch(showModal(
        //   () => <ConfirmActionWarningModal description={inAppText.projectDetailsPage.recordOptions.deleteRecordMessage} />,
        //   {
        //     title: 'Delete Record?',
        //     size: 'md',
        //     buttons: {
        //       component: StackedButtonGroup,
        //       primaryButtonOptions: {
        //         label: 'Delete',
        //         isDisabled: true,
        //         onClick: async () => {
        //           dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isLoading: true } } }));
        //
        //           await dispatch(deleteInsuranceRequirement(requirementId));
        //           dispatch(getInsuredOverallStatus());
        //           dispatch(getActiveJobs(insuredId));
        //           dispatch(hideModal());
        //         }
        //       }
        //     }
        //   }
        // ));
      }
    },
    [Statuses.Active]: {
      icon: BsBoxArrowInUpLeft,
      handler: (value, dispatch, { requirementId, insuredId }) => {
        // const cookieId = 'hideActivateRecordModal';
        //
        // const activateRecord = async () => {
        //   dispatch(hideModal());
        //   createToast({
        //     title: 'Reactivating Record...',
        //     status: 'info',
        //     duration: 10000
        //   });
        //
        //   await dispatch(reactivateInsuranceRequirement(requirementId));
        //   dispatch(getInsuredOverallStatus());
        //   dispatch(getActiveJobs(insuredId));
        // };
        //
        // showDontAskAgainModal({
        //   dispatch,
        //   cookieId,
        //   description: inAppText.projectDetailsPage.recordOptions.reactivateRecordMessage,
        //   onClick: activateRecord,
        //   title: 'Reactivate Record?'
        // });
      }
    }
  },
  attachments: {
    [Statuses.Archived]: {
      handler: async (value, dispatch, { attachmentId, archiveAttachment }) => {
        await archiveAttachment(attachmentId);
      }
    },
    [Statuses.Active]: {
      handler: async (value, dispatch, { attachmentId, activateAttachment }) => {
        await activateAttachment(attachmentId);
      }
    },
    [Statuses.Deleted]: {
      handler: async (value, dispatch, { attachmentId, deleteAttachment }) => {
        await deleteAttachment(attachmentId);
      }
    }
  }
};

const statusButtonTransitions = {
  jobs: {
    [Statuses.Approved]: [
      { status: Statuses.Archived },
      { status: Statuses.Deleted }
    ],
    [Statuses.NotApproved]: [
      { status: Statuses.Archived },
      { status: Statuses.Deleted }
    ],
    [Statuses.Archived]: [
      { status: Statuses.Active },
      { status: Statuses.Deleted }
    ],
    [Statuses.Deleted]: []
  },
  documents: {
    [Statuses.Archived]: [
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.deleteDocument,
        actionText: 'Delete Document'
      },
      {
        status: Statuses.Active,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.activateDocument,
        actionText: 'Activate Document'
      }
    ],
    [Statuses.Expired]: [
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.waiveDocumentRequirement
      }
    ],
    [Statuses.Pending]: [
      {
        status: Statuses.Compliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentCompliant
      },
      {
        status: Statuses.NonCompliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentNonCompliant
      },
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.waiveDocumentRequirement
      }
    ],
    [Statuses.Waived]: [
      {
        status: Statuses.Required,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentRequired,
        actionText: 'Require'
      }
    ],
    [Statuses.NotReceived]: [
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.waiveDocumentRequirement
      },
      {
        status: Statuses.UploadedElsewhere,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.uploadElsewhereDocumentRequirement
      }
    ],
    [Statuses.NotRequired]: [
      {
        status: Statuses.Required,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentRequired,
        actionText: 'Require'
      }
    ],
    [Statuses.NonCompliant]: [
      {
        status: Statuses.Compliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentCompliant
      },
      {
        status: Statuses.Pending,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentPending
      },
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.waiveDocumentRequirement
      }
    ],
    [Statuses.Compliant]: [
      {
        status: Statuses.NonCompliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentNonCompliant
      },
      {
        status: Statuses.Pending,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentPending
      },
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.waiveDocumentRequirement
      }
    ],
    [Statuses.UploadedElsewhere]: [
      {
        status: Statuses.Required,
        dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.markDocumentRequired
      }
    ]
  },
  requirements: {
    [Statuses.Expired]: [
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.waiveRequirement
      },
      {
        status: Statuses.Archived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.archivePolicy,
        actionText: 'Archive Policy'
      },
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.deletePolicy,
        actionText: 'Delete Policy'
      },
      {
        status: Statuses.Canceled,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.cancelPolicy,
        actionText: 'Cancel Policy'
      }
    ],
    [Statuses.NotRequired]: [
      {
        status: Statuses.Required,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyRequired,
        actionText: 'Require'
      },
      {
        status: Statuses.Archived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.archivePolicy,
        actionText: 'Archive Policy'
      },
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.deletePolicy,
        actionText: 'Delete Policy'
      }
    ],
    [Statuses.Pending]: [
      {
        status: Statuses.Compliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyCompliant
      },
      {
        status: Statuses.NonCompliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyNonCompliant
      },
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.waiveRequirement
      },
      {
        status: Statuses.Archived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.archivePolicy,
        actionText: 'Archive Policy'
      },
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.deletePolicy,
        actionText: 'Delete Policy'
      },
      {
        status: Statuses.Canceled,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.cancelPolicy,
        actionText: 'Cancel Policy'
      }
    ],
    [Statuses.Waived]: [
      {
        status: Statuses.Required,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyRequired,
        actionText: 'Require'
      }
    ],
    [Statuses.Compliant]: [
      {
        status: Statuses.NonCompliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyNonCompliant
      },
      {
        status: Statuses.Pending,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyPending
      },
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.waiveRequirement
      },
      {
        status: Statuses.Archived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.archivePolicy,
        actionText: 'Archive Policy'
      },
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.deletePolicy,
        actionText: 'Delete Policy'
      },
      {
        status: Statuses.Canceled,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.cancelPolicy,
        actionText: 'Cancel Policy'
      }
    ],
    [Statuses.NonCompliant]: [
      {
        status: Statuses.Compliant,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyNonCompliant
      },
      {
        status: Statuses.Pending,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyPending
      },
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.waiveRequirement
      },
      {
        status: Statuses.Archived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.archivePolicy,
        actionText: 'Archive Policy'
      },
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.deletePolicy,
        actionText: 'Delete Policy'
      },
      {
        status: Statuses.Canceled,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.cancelPolicy,
        actionText: 'Cancel Policy'
      }
    ],
    [Statuses.Archived]: [
      {
        status: Statuses.Active,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.activatePolicy,
        actionText: 'Activate Policy'
      },
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.deletePolicy,
        actionText: 'Delete Policy'
      }
    ],
    [Statuses.NotReceived]: [
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.waiveRequirement,
        actionText: 'Waive Requirement'
      }
    ]
  },
  notReceivedRequirements: {
    [Statuses.Pending]: [
      {
        status: Statuses.NotReceived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyNotReceived,
        actionText: 'Require'
      }
    ],
    [Statuses.Compliant]: [
      {
        status: Statuses.NotReceived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyNotReceived,
        actionText: 'Mark Not Received'
      }
    ],
    [Statuses.Waived]: [
      {
        status: Statuses.NotReceived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.markPolicyNotReceived,
        actionText: 'Require'
      }
    ],
    [Statuses.NotReceived]: [
      {
        status: Statuses.Waived,
        dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.waiveRequirement,
        actionText: 'Waive Requirement'
      }
    ],
    [Statuses.Expired]: [],
    [Statuses.NonCompliant]: []
  },
  attachments: {
    [Statuses.Archived]: [
      {
        status: Statuses.Deleted,
        dataAnalyticsId: analyticsIds.insuredProfilePage.attachmentsComponent.deleteAttachment,
        actionText: 'Delete Attachment'
      },
      {
        status: Statuses.Active,
        dataAnalyticsId: analyticsIds.insuredProfilePage.attachmentsComponent.activateAttachment,
        actionText: 'Activate Attachment'
      }
    ]
  }
};

export function getStatusButtonMenuOptions(status, statusType) {
  if (!globalStatusOptions[status] || !statusButtonTransitions[statusType][status]) {
    throw new Error(`Status ('${status}') does not exist for ${statusType}`);
  }

  return {
    ...globalStatusOptions[status],
    transitions: arrayFilterAndMap(
      statusButtonTransitions[statusType][status],
      transition => transition !== status && transition.status !== status,
      transition => {
        const transitionHandler = transitionHandlers[statusType][transition.status || transition]
          || transitionHandlers[statusType]['*'];

        const baseStatusOptions = globalStatusOptions[transition.status || transition];

        return {
          ...baseStatusOptions,
          ...transitionHandler,
          actionText: transition.actionText || transitionHandler.actionText || baseStatusOptions.actionText,
          dataAnalyticsId: transition?.dataAnalyticsId
        };
      }
    )
  };
}
